/**
 * What a refused `DELETE /exercise/{id}` means — the PHONE's reading of it, ported (mobile #744,
 * `features/exercises/model/exerciseManage.ts` `exerciseInUseRefusal`), because the owner's rule is
 * that the web says exactly what the phone says.
 *
 * ── Only a LIVE plan refuses a delete (backend #690, library R11) ────────────────────────────
 * Logged history never refuses; a finished or cancelled copy of a plan simply loses the exercise;
 * clips and client goals are deleted with it. What still refuses is a plan somebody can open and
 * edit today, and the refusal is a 409 carrying `code: exercise_in_use` and a body that NAMES those
 * plans: `plans` (at most ten of the caller's own), `planCount` (the true total), and the two parts
 * of that total the caller cannot act on — `formerClientPlanCount` and `otherTrainerPlanCount`.
 *
 * ── Every 409 is in use; anything else is a failure worth retrying ──────────────────────────
 * A backend from before the typed refusal answers a bare 409 (`referential-conflict`) that names
 * nothing; it is still „in a plan”, with `planCount: null` so no sentence claims a count it does not
 * have. A typed body is VALIDATED rather than trusted — an odd shape degrades to that same
 * nothing-named refusal, never to a throw, because the alternative on screen is „spróbuj ponownie”,
 * advice that can never work here. Any other status (or none, a request that never arrived) is
 * `null`: the dialog's „failed” phase and its retry (D24).
 *
 * Pure: the status and the problem body arrive as parameters; the caller reads them off `ApiError`.
 */

import type {
  TrainWithMeModulesExercisesDtosResponsesExerciseInUseProblemResponse as ExerciseInUseProblemWire,
  TrainWithMeModulesExercisesDtosResponsesPlanUsingExerciseResponse as PlanUsingExerciseWire,
} from '~/shared/api/generated/model';

/** One plan the refusal names. */
export interface PlanUsingExercise {
  readonly name: string;
  /** The client whose copy this is; `null` for a library plan (or a client with no name on record). */
  readonly customerName: string | null;
}

/** A delete refused because live plans still hold the exercise. See the header. */
export interface ExerciseInUseRefusal {
  readonly plans: readonly PlanUsingExercise[];
  /** The true total, or `null` when the refusal names nothing (an older backend). */
  readonly planCount: number | null;
  /** Plans of clients this trainer no longer coaches — they clear themselves. Never named. */
  readonly formerClientPlanCount: number;
  /** Plans another trainer authored — only their author can clear them. Never named. */
  readonly otherTrainerPlanCount: number;
}

const HTTP_CONFLICT = 409;
const EXERCISE_IN_USE_CODE = 'exercise_in_use';
const UNNAMED_REFUSAL: ExerciseInUseRefusal = {
  plans: [],
  planCount: null,
  formerClientPlanCount: 0,
  otherTrainerPlanCount: 0,
};

/** A field off an untyped body, without trusting its shape. */
function field(body: unknown, key: string): unknown {
  return typeof body === 'object' && body !== null ? Reflect.get(body, key) : undefined;
}

/**
 * The in-use refusal a failed delete carries, or `null` when the failure is something else.
 *
 * Read through the generated problem's own field NAMES (`keyof`), so a backend rename fails the build
 * here rather than silently dropping every plan; the VALUES are each checked, because the body may
 * equally have come from a backend that never heard of this problem.
 */
export function exerciseInUseRefusal(
  status: number | null,
  problem: unknown,
): ExerciseInUseRefusal | null {
  if (status !== HTTP_CONFLICT) return null;
  const key = (name: Extract<keyof ExerciseInUseProblemWire, string>) => field(problem, name);
  const plans = key('plans');
  if (field(problem, 'code') !== EXERCISE_IN_USE_CODE || !Array.isArray(plans)) {
    return UNNAMED_REFUSAL;
  }

  const named = plans.flatMap(planUsingExerciseFromWire);
  const total = Math.max(wholeCount(key('planCount')), named.length);
  // A refusal counting NOTHING is not a count: something refused this delete, and „0 plans” is the
  // one thing it cannot have been.
  if (total === 0) return UNNAMED_REFUSAL;

  // Both untouchable buckets are fitted into what is left once the NAMED plans (the caller's own by
  // definition) are accounted for, in a fixed order, so a body contradicting itself cannot turn a
  // refusal the trainer can act on into one they cannot, nor read two ways.
  const room = total - named.length;
  const formerClient = Math.min(wholeCount(key('formerClientPlanCount')), room);
  return {
    plans: named,
    planCount: total,
    formerClientPlanCount: formerClient,
    otherTrainerPlanCount: Math.min(wholeCount(key('otherTrainerPlanCount')), room - formerClient),
  };
}

/** A count the backend stated as a whole number of things, or 0 — never negative, fractional or a word. */
function wholeCount(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : 0;
}

function planUsingExerciseFromWire(entry: unknown): PlanUsingExercise[] {
  const wire = (name: Extract<keyof PlanUsingExerciseWire, string>) => field(entry, name);
  const name = wire('name');
  const customerName = wire('customerName');
  if (typeof name !== 'string' || name.trim() === '') return [];
  return [
    {
      name,
      customerName:
        typeof customerName === 'string' && customerName.trim() !== '' ? customerName : null,
    },
  ];
}
